import { Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomSwitch from "../../../components/CustomSwitch";
import Select from "../../../components/Select/Select";
import {
  createApprovalRules,
  getApprovalRules,
  updateApprovalRules,
} from "../../../redux/reducers/globalSettingsSlice";
import { showSnackBar } from "../../../redux/reducers/snackSlice";
import { getSessionData } from "../../../util/function";
import { readMandateSession } from "../../../util/mandateSessionStorage";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const roleOptions = [
  { code: "INVENTORY_MANAGER", label: "Inventory Manager" },
  { code: "BRANCH_MANAGER", label: "Practice Manager" },
  { code: "CLINIC_OWNER", label: "Clinic Owner" },
  { code: "ROLE_CLINICSUPERADMIN", label: "Super Admin" },
];

const isOwnerLevelRole = (role) =>
  role === "CLINIC_OWNER" || role === "ROLE_CLINICSUPERADMIN";

const isClinicOwnerOnly = (roles = []) => {
  const list = Array.isArray(roles) ? roles : [];
  return list?.length > 0 && list?.every(isOwnerLevelRole);
};

const ROLE_TO_USER_TYPES = {
  INVENTORY_MANAGER: ["ROLE_INVENTORY"],
  BRANCH_MANAGER: ["ROLE_CLINICADMIN"], //ROLE_CLINIC
  CLINIC_OWNER: ["ROLE_CLINIC"], //ROLE_ADMIN
  ROLE_CLINICSUPERADMIN: ["ROLE_CLINICSUPERADMIN"],
};

const SETTING_TO_RULE_TYPE = {
  newInventoryItem: "INVENTORY_NEW",
  newInventoryBatch: "INVENTORY_NEW_BATCH",
  newServiceItem: "SERVICE_NEW",
  newServiceVariant: "SERVICE_NEW_VARIANT",
  transferSameBranch: "INTERNAL_TRANSFER",
  transferBetweenBranches: "EXTERNAL_TRANSFER",
  manualStockAdjustment: "COUNT_CORRECTION",
  purchaseOrder: "PURCHASE_ORDER",
};

const RULE_TYPE_ALIASES = {
  INTERNAL_TRANSFER: ["INTERNAL_TRANSFER", "INTERNAL_TRASFER"],
  EXTERNAL_TRANSFER: ["EXTERNAL_TRANSFER", "EXTERNAL_TRASFER"],
  INVENTORY_NEW: ["INVENTORY_NEW"],
  INVENTORY_NEW_BATCH: ["INVENTORY_NEW_BATCH"],
  SERVICE_NEW: ["SERVICE_NEW"],
  SERVICE_NEW_VARIANT: ["SERVICE_NEW_VARIANT"],
  COUNT_CORRECTION: ["COUNT_CORRECTION"],
  PURCHASE_ORDER: ["PURCHASE_ORDER"],
};

const RULE_TYPE_TO_SETTING = {
  INVENTORY_NEW: "newInventoryItem",
  INVENTORY_NEW_BATCH: "newInventoryBatch",
  SERVICE_NEW: "newServiceItem",
  SERVICE_NEW_VARIANT: "newServiceVariant",
  INTERNAL_TRANSFER: "transferSameBranch",
  INTERNAL_TRASFER: "transferSameBranch",
  EXTERNAL_TRANSFER: "transferBetweenBranches",
  EXTERNAL_TRASFER: "transferBetweenBranches",
  COUNT_CORRECTION: "manualStockAdjustment",
  PURCHASE_ORDER: "purchaseOrder",
};

const INVENTORY_RULE_TYPES = [
  "INVENTORY_NEW",
  "INVENTORY_NEW_BATCH",
  "SERVICE_NEW",
  "SERVICE_NEW_VARIANT",
  "INTERNAL_TRANSFER",
  "INTERNAL_TRASFER",
  "EXTERNAL_TRANSFER",
  "EXTERNAL_TRASFER",
  "COUNT_CORRECTION",
  "PURCHASE_ORDER",
];

const DEFAULT_SETTINGS = {
  newInventoryItem: { mode: "auto", roles: [], users: [] },
  newInventoryBatch: { mode: "auto", roles: [], users: [] },
  newServiceItem: { mode: "auto", roles: [], users: [] },
  newServiceVariant: { mode: "auto", roles: [], users: [] },
  transferSameBranch: { mode: "auto", roles: [], users: [] },
  transferBetweenBranches: { mode: "auto", roles: [], users: [] },
  manualStockAdjustment: { mode: "auto", roles: [], users: [] },
  purchaseOrder: { mode: "auto", roles: [], users: [] },
};

const getUserOptionId = (user) => {
  if (user == null) return "";
  if (typeof user === "object") {
    return String(
      user?.value ?? user?.id ?? user?.userId ?? user?.doctorId ?? "",
    );
  }
  return String(user);
};

const getUserRoleNames = (user) => {
  const types = user?.userType || user?.roles || user?.roleList || [];
  if (!Array.isArray(types)) return [];
  return types
    .map((role) => (typeof role === "string" ? role : role?.name))
    .filter(Boolean);
};

const toUserIds = (users = []) =>
  users
    .map((user) => Number(getUserOptionId(user)))
    .filter((id) => !Number.isNaN(id) && id > 0);

const extractInventoryRules = (payload) => {
  if (Array.isArray(payload)) {
    return payload.filter((item) =>
      INVENTORY_RULE_TYPES.includes(item?.ruleType),
    );
  }

  const rules = payload?.rules;
  if (Array.isArray(rules)) {
    return rules.filter((item) =>
      INVENTORY_RULE_TYPES.includes(item?.ruleType),
    );
  }

  if (rules && typeof rules === "object") {
    return Object.values(SETTING_TO_RULE_TYPE)
      .map((ruleType) => {
        const aliases = RULE_TYPE_ALIASES[ruleType] || [ruleType];
        const matchedKey = aliases.find((key) => rules[key]);
        const value = matchedKey ? rules[matchedKey] : null;
        if (!value) return null;
        if (Array.isArray(value)) {
          return {
            ruleType,
            category: "Inventory",
            autoApproval: false,
            rules: value,
          };
        }
        return {
          ruleType,
          category: value?.category || "Inventory",
          autoApproval:
            value?.autoApproval == null ? true : !!value?.autoApproval,
          rules: Array.isArray(value?.rules) ? value.rules : [],
        };
      })
      .filter(Boolean);
  }

  return [];
};

const InventorySettings = () => {
  const dispatch = useDispatch();
  const branches = useMemo(() => {
    const sessionBranches = readMandateSession()?.clinicBranches;
    return Array.isArray(sessionBranches) ? sessionBranches : [];
  }, []);
  const user = localStorage.getItem("user");
  const profile = useMemo(() => {
    try {
      return user ? JSON.parse(user) : {};
    } catch {
      return {};
    }
  }, [user]);
  const globalSettingsData =
    readMandateSession()?.dataSharing?.globalSettingsData ?? false;
  const showSharedTag =
    branches?.some((li) => li?.id === profile?.id && li?.branchNumber) &&
    (globalSettingsData === true || globalSettingsData === "true");
  const [saving, setSaving] = useState(false);
  const [hasExistingRules, setHasExistingRules] = useState(false);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const usersList = useMemo(() => {
    const list = getSessionData()?.vets ?? [];
    return (Array.isArray(list) ? list : []).filter((user) =>
      (user?.userType || []).some((role) => role?.name !== "ROLE_DOCTOR"),
    );
  }, []);

  const dataSharing =
    readMandateSession()?.dataSharing ?? getSessionData()?.dataSharing ?? {};
  const allowBranchStockEdit = !!dataSharing?.allowBranchStockEdit;
  const allowBranchServiceEdit = !!dataSharing?.allowBranchServiceEdit;
  const showMasterItemCreation = allowBranchStockEdit || allowBranchServiceEdit;

  const getUsersForRoles = useCallback(
    (roles = []) => {
      if (!roles.length) return [];
      const allowedTypeSet = new Set(
        roles.flatMap((role) => ROLE_TO_USER_TYPES[role] || [role]),
      );

      const matched = usersList.filter((user) => {
        const roleNames = getUserRoleNames(user);
        if (!roleNames.length) return false;
        return roleNames.some((name) => allowedTypeSet.has(name));
      });

      return matched
        .map((user) => {
          const id = user?.id ?? user?.userId ?? user?.doctorId;
          return {
            name:
              user?.name ||
              user?.userName ||
              user?.fullName ||
              `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
              `User ${id}`,
            value: String(id),
          };
        })
        .filter((user) => user.value && user.value !== "undefined");
    },
    [usersList],
  );

  const applyApiRulesToSettings = useCallback((inventoryRules = []) => {
    setSettings((prev) => {
      const next = { ...prev };
      inventoryRules.forEach((rule) => {
        const settingKey = RULE_TYPE_TO_SETTING[rule?.ruleType];
        if (!settingKey || !next[settingKey]) return;

        const firstRule = Array.isArray(rule?.rules) ? rule.rules[0] : null;
        const roles = Array.isArray(firstRule?.roles)
          ? firstRule.roles.filter(Boolean)
          : prev[settingKey].roles;
        const users = Array.isArray(firstRule?.userIds)
          ? firstRule.userIds
              .map((id) => String(id))
              .filter((id) => id && id !== "undefined" && id !== "null")
          : [];

        next[settingKey] = {
          ...prev[settingKey],
          mode:
            rule?.autoApproval == null
              ? "auto"
              : rule.autoApproval
                ? "auto"
                : "approval",
          roles: roles.length > 0 ? roles : [],
          users,
        };
      });
      return next;
    });
  }, []);

  const loadApprovalRules = useCallback(async () => {
    const result = await dispatch(getApprovalRules());
    if (!getApprovalRules.fulfilled.match(result)) return;

    const inventoryRules = extractInventoryRules(result.payload);
    setHasExistingRules(inventoryRules.length > 0);
    if (inventoryRules.length > 0) {
      applyApiRulesToSettings(inventoryRules);
    }
  }, [applyApiRulesToSettings, dispatch]);

  useEffect(() => {
    loadApprovalRules();
  }, [loadApprovalRules]);

  const toggleMode = (key, mode) => {
    setSettings((prev) => ({
      ...prev,
      [key]: { ...prev[key], mode },
    }));
  };

  const toggleRole = (key, role) => {
    setSettings((prev) => {
      const exists = prev[key].roles.includes(role);
      const nextRoles = exists
        ? prev[key].roles.filter((r) => r !== role)
        : [...prev[key].roles, role];
      const allowedUsers = new Set(
        getUsersForRoles(nextRoles).map((u) => String(u.value)),
      );
      return {
        ...prev,
        [key]: {
          ...prev[key],
          roles: nextRoles,
          users: isClinicOwnerOnly(nextRoles)
            ? []
            : (prev[key].users || []).filter((u) =>
                allowedUsers.has(getUserOptionId(u)),
              ),
        },
      };
    });
  };

  const updateUsers = (key, users) => {
    setSettings((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        users: (users || [])
          .map((user) => getUserOptionId(user))
          .filter((id) => id && id !== "undefined" && id !== "null"),
      },
    }));
  };

  const buildSavePayload = () =>
    Object.entries(SETTING_TO_RULE_TYPE).map(([settingKey, ruleType]) => {
      const item = settings[settingKey] || {};
      const isHiddenMasterItem =
        ((settingKey === "newInventoryItem" ||
          settingKey === "newInventoryBatch") &&
          !allowBranchStockEdit) ||
        ((settingKey === "newServiceItem" ||
          settingKey === "newServiceVariant") &&
          !allowBranchServiceEdit);
      const autoApproval = isHiddenMasterItem || item.mode === "auto";
      return {
        autoApproval,
        category: "Inventory",
        ruleType,
        rules: autoApproval
          ? []
          : [
              {
                roles: item.roles || [],
                userIds: toUserIds(item.users || []),
              },
            ],
      };
    });

  const handleSave = async () => {
    const settingLabels = {
      newInventoryItem: "New inventory item",
      newInventoryBatch: "New inventory batch",
      newServiceItem: "New service item",
      newServiceVariant: "New service variant",
      transferSameBranch: "Within the same branch",
      transferBetweenBranches: "Between branches",
      manualStockAdjustment: "Manual stock count correction",
      purchaseOrder: "Purchase order",
    };

    for (const [key, item] of Object.entries(settings)) {
      if (
        (key === "newInventoryItem" || key === "newInventoryBatch") &&
        !allowBranchStockEdit
      )
        continue;
      if (
        (key === "newServiceItem" || key === "newServiceVariant") &&
        !allowBranchServiceEdit
      )
        continue;
      const needsApproval = item.mode === "approval";
      if (!needsApproval) continue;
      if (!item.roles?.length && !item.users?.length) {
        dispatch(
          showSnackBar({
            message: `${settingLabels[key] || key}: select at least one role or specific person`,
            type: "error",
          }),
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = buildSavePayload();
      const result = await dispatch(
        hasExistingRules
          ? updateApprovalRules(payload)
          : createApprovalRules(payload),
      );
      const matched = hasExistingRules
        ? updateApprovalRules.fulfilled.match(result)
        : createApprovalRules.fulfilled.match(result);
      if (!matched) return;

      setHasExistingRules(true);
      await loadApprovalRules();
    } finally {
      setSaving(false);
    }
  };

  const renderApprovalGateRow = (key, title, desc, isLast) => {
    const item = settings[key];
    const isApprovalNeeded = item.mode === "approval";
    const autoApprove = item.mode === "auto";
    const showPersonSelect = !isClinicOwnerOnly(item.roles);
    const personOptions = showPersonSelect ? getUsersForRoles(item.roles) : [];
    const selectedUserIds = new Set(
      (item.users || []).map((user) => getUserOptionId(user)),
    );
    const selectedPersons = personOptions.filter((option) =>
      selectedUserIds.has(String(option.value)),
    );

    return (
      <div className={`py15 ${!isLast ? "border-bottom" : ""}`} key={key}>
        <div
          className="flex-row-between mb10 gap15"
          style={{ alignItems: "flex-start" }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography className="text-bold fs14 black">{title}</Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              {desc}
            </Typography>
          </div>

          <div
            className="bf-approve-switch flex-shrink-0"
            style={{ marginTop: 2 }}
          >
            <Typography
              className={`txt-regular fs12 ${
                autoApprove ? "blue-color" : "card-gray-color"
              }`}
            >
              Auto approve
            </Typography>
            <CustomSwitch
              value={isApprovalNeeded}
              onChange={(e) =>
                toggleMode(key, e?.target?.value ? "approval" : "auto")
              }
              greenToGray
              disabled={showSharedTag}
            />
            <Typography
              className={`txt-regular fs12 ${
                isApprovalNeeded ? "blue-color" : "card-gray-color"
              }`}
            >
              Requires approval
            </Typography>
          </div>
        </div>

        {isApprovalNeeded && (
          <div className="bf-tier-card mt5">
            <div>
              <Typography className="txt-regular fs12 card-gray-color mb8">
                By role
              </Typography>
              <div className="bf-chip-row">
                {roleOptions.map((role) => {
                  const selected = item.roles.includes(role.code);
                  return (
                    <button
                      key={role.code}
                      type="button"
                      className={`bf-role-chip${
                        selected ? " bf-role-chip-active" : ""
                      }`}
                      onClick={() => toggleRole(key, role.code)}
                      disabled={showSharedTag}
                    >
                      <span className="bf-chip-check">
                        {selected ? "✓" : ""}
                      </span>
                      {role.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {showPersonSelect ? (
              <div className="mt15">
                <Typography className="txt-regular fs12 card-gray-color mb8">
                  By specific person (optional if a role is selected)
                </Typography>
                <div className="bf-person-select">
                  <Select
                    multiSelectSearchCheck
                    label=""
                    list={personOptions}
                    value={selectedPersons}
                    handleChange={(e) =>
                      updateUsers(key, e?.target?.value || [])
                    }
                    disabled={showSharedTag || !item.roles?.length}
                  />
                </div>
                {!item.roles?.length ? (
                  <Typography className="txt-regular fs11 card-gray-color mt6">
                    Select at least one role to choose people.
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10 border-bottom">
        <div>
          <div className="flex-row-ali-cen">
            <Typography className="text-bold fs20 black">Inventory</Typography>
            {showSharedTag ? (
              <Typography className="white-color fs12 con-fit card-top-orange-color ph10 br10 ml10">
                Organisation default
              </Typography>
            ) : null}
          </div>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Approval gates for master data creation, stock transfers,
            adjustments, and purchase orders.
          </Typography>
        </div>
        <div className="flex-row-ali-cen gap10">
          <CustomButton
            text="Save Changes"
            onClick={handleSave}
            smallBtn
            disabled={saving || showSharedTag}
            grayBtn={saving || showSharedTag}
          />
        </div>
      </div>

      <div className="back-white br12 border">
        {showMasterItemCreation ? (
          <div className="mb20">
            <Typography className="text-bold fs11 gray6 uppercase tracking-wider mb10">
              Master item creation
            </Typography>
            {allowBranchStockEdit
              ? renderApprovalGateRow(
                  "newInventoryItem",
                  "New inventory item",
                  "A new stock-keeping item (medicine, consumable) requires sign-off before it becomes usable.",
                  false,
                )
              : null}
            {allowBranchStockEdit
              ? renderApprovalGateRow(
                  "newInventoryBatch",
                  "New inventory batch",
                  "A new inventory batch requires sign-off before it can be used.",
                  false,
                )
              : null}
            {allowBranchServiceEdit
              ? renderApprovalGateRow(
                  "newServiceItem",
                  "New service item",
                  "A new billable service line requires sign-off before it can be added to invoices.",
                  false,
                )
              : null}
            {allowBranchServiceEdit
              ? renderApprovalGateRow(
                  "newServiceVariant",
                  "New service variant",
                  "A new service variant requires sign-off before it can be added to invoices.",
                  false,
                )
              : null}
          </div>
        ) : null}

        <div className="mb20">
          <Typography className="text-bold fs11 gray6 uppercase tracking-wider mb10">
            Stock transfers
          </Typography>
          {renderApprovalGateRow(
            "transferSameBranch",
            "Within the same branch",
            "Stock moved between locations inside one branch (e.g. store room → dispensary).",
            false,
          )}
          {renderApprovalGateRow(
            "transferBetweenBranches",
            "Between branches",
            "Stock transferred to or from another branch.",
            false,
          )}
        </div>

        <div className="mb20">
          <Typography className="text-bold fs11 gray6 uppercase tracking-wider mb10">
            Stock adjustments
          </Typography>
          {renderApprovalGateRow(
            "manualStockAdjustment",
            "Manual stock count correction",
            "Adjustments change the system-of-record inventory count — the main control point against shrinkage.",
            true,
          )}
        </div>

        <div>
          <Typography className="text-bold fs11 gray6 uppercase tracking-wider mb10">
            Purchase order
          </Typography>
          {renderApprovalGateRow(
            "purchaseOrder",
            "Purchase order",
            "A purchase order requires sign-off before it can be sent to a supplier.",
            true,
          )}
        </div>
      </div>
    </div>
  );
};

export default InventorySettings;
